/**
 * Choosing an audio or subtitle track, mid-film.
 *
 * ## The gap this closes
 *
 * Before this, language preferences were applied once, silently, at the moment the engine
 * attached — and only on hls.js and AVPlay. Nothing surfaced what had been chosen, nothing
 * listed the alternatives, and a viewer who wanted the other audio track had no way to ask
 * for it. On a household television that is the difference between a film being watchable
 * and not: the preference belongs to the profile, and two people on the sofa do not have
 * two profiles selected.
 *
 * ## Why the empty states are as carefully written as the list
 *
 * Three engines — `native`, `remux` and `mpegts` — genuinely cannot enumerate tracks.
 * `HTMLMediaElement.audioTracks` is unimplemented in Chrome and Firefox, so there is no
 * list to read and no amount of work here produces one. A menu that renders empty in that
 * case looks broken and invites the viewer to keep pressing it. Saying *why* costs one
 * sentence and is the difference between a limitation and a fault.
 *
 * The same applies to a stream that simply has one audio track and no subtitles, which is
 * most of a VOD catalogue. That is a different sentence, because it is a different fact.
 *
 * ## Why it re-reads rather than receiving a list
 *
 * hls.js does not know its own tracks until `MANIFEST_PARSED`, seconds after attach. A
 * component handed a snapshot at mount would be permanently empty on the one engine that
 * supports this best.
 */
import { useEffect, useState } from 'react';
import type { EngineTracks, TrackSet } from './engines';
import { Icon } from '../components/Icon';

export interface TrackMenuProps {
  /**
   * The engine's track interface, or null where it has none.
   *
   * Null is a first-class case, not an error: it is the honest state for three of the five
   * engines and it gets its own explanation below.
   */
  tracks: EngineTracks | null;
  /** Which engine is attached, so the "cannot list" case can say something specific. */
  engine?: string | null;
  onClose: () => void;
  /** TV gets larger rows and no pointer affordances; the content is identical. */
  tv?: boolean;
}

/**
 * One section — the audio tracks, or the subtitles plus their "off" row.
 *
 * Rendered as plain `<button>`s with no focus handling of their own. On the television
 * `AutoFocusables` registers them with the same spatial navigation the rest of the app
 * uses, so the DPAD reaches them without this component knowing anything about focus.
 */
function Section({
  title,
  rows,
  active,
  onPick,
}: {
  title: string;
  rows: { id: string | null; label: string }[];
  active: string | null;
  onPick: (id: string | null) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="trackmenu__section">
      <h3 className="trackmenu__heading">{title}</h3>
      <ul className="trackmenu__list">
        {rows.map((row) => {
          const selected = row.id === active;
          return (
            <li key={row.id ?? 'off'}>
              <button
                type="button"
                className={`trackmenu__row${selected ? ' is-selected' : ''}`}
                // `aria-pressed` rather than `aria-checked`: these are buttons, and a
                // screen reader announcing "not checked" for every alternative reads as
                // though they were all switched off.
                aria-pressed={selected}
                onClick={() => onPick(row.id)}
              >
                {/* The tick occupies its slot whether or not it is drawn, so selecting a
                    row does not shift the label a few pixels sideways. */}
                <span className="trackmenu__tick">
                  {selected && <Icon name="check" size={0.9} />}
                </span>
                {row.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function TrackMenu({ tracks, engine, onClose, tv = false }: TrackMenuProps) {
  /*
   * Held in state and refreshed on notification, rather than read through
   * `useSyncExternalStore`.
   *
   * That hook compares snapshots by identity and `EngineTracks.get()` builds a fresh
   * object on every call — labelling is derived, not stored — so it would re-render
   * without end. Caching a snapshot inside the engine to satisfy it would push a React
   * detail down into the playback layer, which has enough to be right about already.
   */
  const [set, setSet] = useState<TrackSet | null>(() => tracks?.get() ?? null);

  useEffect(() => {
    if (!tracks) {
      setSet(null);
      return;
    }
    const read = () => setSet(tracks.get());
    read();
    return tracks.subscribe(read);
  }, [tracks]);

  const audio = set?.audio ?? [];
  const subtitle = set?.subtitle ?? [];
  // One audio track is not a choice. Listing it anyway would offer a menu whose only row
  // is already selected and does nothing when pressed.
  const hasAudioChoice = audio.length > 1;
  const nothingToOffer = !hasAudioChoice && subtitle.length === 0;

  return (
    <div className={`trackmenu${tv ? ' trackmenu--tv' : ''}`} role="dialog" aria-label="Ses ve altyazı">
      <div className="trackmenu__bar">
        <h2 className="trackmenu__title">Ses ve altyazı</h2>
        {/*
          No close button on the television, and the reason is focus rather than taste.
          `AutoFocusables` claims the first focusable inside a `role="dialog"` when the
          menu appears, so a close button here would be the row the remote lands on —
          the viewer opens a track list and the highlight is sitting on "leave". Back
          already closes it, which is the platform's own convention and what the hint
          below says. On web the pointer needs a visible way out, so it stays.
        */}
        {tv ? (
          <span className="trackmenu__hint">Geri: kapat</span>
        ) : (
          <button type="button" className="trackmenu__close" onClick={onClose} aria-label="Kapat">
            <Icon name="close" size={0.9} />
          </button>
        )}
      </div>

      {tracks === null ? (
        <p className="trackmenu__empty">
          Bu oynatıcı parça listesi veremiyor
          {engine ? ` (${engine})` : ''}. Tarayıcılar bir dosyanın içindeki ses parçalarını
          uygulamaya açmıyor, bu yüzden yayın kendi varsayılan sesiyle oynuyor.
        </p>
      ) : nothingToOffer ? (
        <p className="trackmenu__empty">Bu yayında tek ses parçası var, altyazı yok.</p>
      ) : (
        <>
          {hasAudioChoice && (
            <Section
              title="Ses"
              rows={audio}
              active={set?.activeAudio ?? null}
              onPick={(id) => tracks.select('audio', id)}
            />
          )}
          {subtitle.length > 0 && (
            <Section
              title="Altyazı"
              // "Kapalı" is a row rather than a toggle elsewhere: it is one of the answers
              // to the same question, and it has to be tickable so the viewer can see that
              // off is what is currently true.
              rows={[{ id: null, label: 'Kapalı' }, ...subtitle]}
              active={set?.activeSubtitle ?? null}
              onPick={(id) => tracks.select('subtitle', id)}
            />
          )}
        </>
      )}
    </div>
  );
}
